/**
 * Checkout contact-form persistence.
 *
 * The booking cart survives a reload (BookingFlow persists bookingData to
 * sessionStorage + a localStorage backup), but the Checkout contact form
 * (first/last name, email, phone) lived only in component-local state. So a
 * full-page reload on the payment step — most notably an iOS Safari tab-discard
 * when the heavy Stripe Elements mount — silently wiped the customer's details.
 *
 * This persists those details (scoped per merchant) so a reload no longer loses
 * them. Notes:
 *   - PII (name/email/phone) is stored only in the customer's own browser.
 *   - Primary store is per-tab sessionStorage; a short-TTL localStorage backup
 *     covers redirect/discard round-trips without lingering indefinitely.
 *   - Cleared on a completed booking and when the customer starts over
 *     (BookingFlow's return-to-locations reset).
 *   - Empty/near-empty forms are never written (and clear any prior value), so
 *     a fresh checkout still gets its normal browser-country prefill.
 */

const STORAGE_PREFIX = 'checkoutUserInfo';
const key = (merchantId) => `${STORAGE_PREFIX}-${merchantId}`;
const TTL_MS = 2 * 60 * 60 * 1000; // 2 hours (matches the cart backup window)

// Only persist a form the customer has actually started filling. A bare country
// default (no name/email/phone) is treated as empty.
const hasContent = (u) =>
  !!u && (
    (u.firstName && u.firstName.trim()) ||
    (u.lastName && u.lastName.trim()) ||
    (u.email && u.email.trim()) ||
    (u.phone && u.phone.replace(/[^0-9]/g, '').length > 0)
  );

export const clearCheckoutUserInfo = (merchantId) => {
  const k = key(merchantId);
  try { sessionStorage.removeItem(k); } catch { /* storage unavailable */ }
  try { localStorage.removeItem(`${k}-backup`); } catch { /* storage unavailable */ }
};

export const readCheckoutUserInfo = (merchantId) => {
  const k = key(merchantId);
  let parsed = null;
  try {
    const s = sessionStorage.getItem(k);
    if (s) parsed = JSON.parse(s);
  } catch { parsed = null; }
  if (!parsed) {
    try {
      const raw = localStorage.getItem(`${k}-backup`);
      if (raw) {
        const b = JSON.parse(raw);
        if (b?.data && b?.ts && Date.now() - b.ts < TTL_MS) parsed = JSON.parse(b.data);
      }
    } catch { parsed = null; }
  }
  return hasContent(parsed) ? parsed : null;
};

export const writeCheckoutUserInfo = (merchantId, userInfo) => {
  if (!hasContent(userInfo)) {
    clearCheckoutUserInfo(merchantId);
    return;
  }
  const k = key(merchantId);
  try {
    const serialized = JSON.stringify(userInfo);
    sessionStorage.setItem(k, serialized);
    try {
      localStorage.setItem(`${k}-backup`, JSON.stringify({ ts: Date.now(), data: serialized }));
    } catch { /* localStorage may throw (private mode / quota) */ }
  } catch { /* storage unavailable */ }
};
